import {
  AI_TOOL_DECLARATIONS,
  AiToolExecutor,
} from '@spblock/core';
import type * as Blockly from 'blockly/core';
import { useStore, type AiMessage, type AiToolCall } from '../state/store';

/**
 * Google AI Studio (Gemini) client with a function-calling tool loop.
 * The API key never leaves the user's browser except to Google's endpoint.
 */

export const GEMINI_ENDPOINT = 'https://generativelanguage.googleapis.com/v1beta/models';

export const SYSTEM_INSTRUCTION = `You are the assistant inside SPblock, a visual block editor for the ESP32 (Arduino framework, Arduino-ESP32 core 3.x).

You are a canvas actor, not a chatbot:
- Resolve requests by manipulating the canvas with tools. Do not describe what you would build — build it.
- Always call get_canvas_state before editing, and list_block_types when unsure about block types or legal field values.
- Never invent block types or field values. Only block types returned by list_block_types are valid; dropdown fields accept only their listed options.
- Programs need event blocks: put configuration (pin modes, PWM setup, I2C begin, WiFi connect, MQTT setup, serial begin) inside "when board starts" (esp32_on_setup), and main logic inside "forever, every cycle" (esp32_on_loop).
- Hardware facts: pins 34-39 are input-only; analog read only works on ADC1 pins (32, 33, 34, 35, 36, 39); avoid GPIO 0, 2, 12, 15 as outputs at boot; use "every N ms" instead of long delay() loops when responsiveness matters.
- When the user reports a problem, call get_diagnostics, reason about the generated code, then fix the canvas.
- Keep prose short and technical. Never use emojis. After editing the canvas, summarize what you changed in one or two sentences.`;

type Part =
  | { text: string }
  | { functionCall: { name: string; args: Record<string, unknown> } }
  | { functionResponse: { name: string; response: Record<string, unknown> } };

interface Content {
  role: 'user' | 'model';
  parts: Part[];
}

export interface AssistantTurnResult {
  text: string;
  toolCalls: AiToolCall[];
}

export class GeminiError extends Error {}

async function callGemini(model: string, apiKey: string, body: unknown): Promise<Response> {
  const res = await fetch(`${GEMINI_ENDPOINT}/${model}:generateContent?key=${encodeURIComponent(apiKey)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try {
      const data = (await res.json()) as { error?: { message?: string; status?: string } };
      if (data.error?.message) detail = data.error.message;
      else if (data.error?.status) detail = data.error.status;
    } catch {
      /* keep HTTP status */
    }
    if (res.status === 400 && /api key/i.test(detail)) {
      throw new GeminiError('The API key was rejected. Check it in Settings.');
    }
    if (res.status === 403) {
      throw new GeminiError('Access denied for this API key. Verify the Generative Language API is enabled for it.');
    }
    if (res.status === 429) {
      throw new GeminiError('Rate limit reached for this API key. Wait a moment and try again.');
    }
    throw new GeminiError(`Gemini request failed: ${detail}`);
  }
  return res;
}

export async function runAssistantTurn(options: {
  apiKey: string;
  model: string;
  workspace: Blockly.Workspace;
  history: AiMessage[];
  userText: string;
  onToolCall?: (call: AiToolCall) => void;
}): Promise<AssistantTurnResult> {
  const { apiKey, model, workspace, history, userText, onToolCall } = options;

  const contents: Content[] = [];
  for (const message of history) {
    if (message.role === 'user' && message.text) {
      contents.push({ role: 'user', parts: [{ text: message.text }] });
    } else if (message.role === 'assistant' && message.text) {
      contents.push({ role: 'model', parts: [{ text: message.text }] });
    }
  }
  contents.push({ role: 'user', parts: [{ text: userText }] });

  const executor = new AiToolExecutor(workspace);
  const toolCalls: AiToolCall[] = [];

  for (let round = 0; round < 8; round++) {
    const res = await callGemini(model, apiKey, {
      contents,
      systemInstruction: { parts: [{ text: SYSTEM_INSTRUCTION }] },
      tools: [{ functionDeclarations: AI_TOOL_DECLARATIONS }],
      generationConfig: { temperature: 0.2 },
    });
    const data = (await res.json()) as {
      candidates?: { content?: { parts?: Part[] }; finishReason?: string }[];
    };
    const candidate = data.candidates?.[0];
    const parts = candidate?.content?.parts ?? [];

    let text = '';
    let sawToolCall = false;
    for (const part of parts) {
      if ('text' in part) {
        text += part.text;
      } else if ('functionCall' in part) {
        sawToolCall = true;
        const call = part.functionCall;
        const result = executor.execute(call.name, call.args ?? {});
        const summary =
          result.message.length > 140 ? `${result.message.slice(0, 140)}…` : result.message;
        const record = { name: call.name, ok: result.ok, summary };
        toolCalls.push(record);
        onToolCall?.(record);
        contents.push({ role: 'model', parts: [{ functionCall: call }] });
        contents.push({
          role: 'user',
          parts: [{ functionResponse: { name: call.name, response: result as unknown as Record<string, unknown> } }],
        });
      }
    }

    if (!sawToolCall) {
      return { text: text.trim() || '(no response)', toolCalls };
    }
  }

  throw new GeminiError('The assistant exceeded its tool round limit without finishing. Ask it to continue.');
}

/** One cheap request to validate a key against the Models endpoint. */
export async function testApiKey(apiKey: string): Promise<{ ok: boolean; message: string }> {
  try {
    const res = await fetch(`${GEMINI_ENDPOINT}?key=${encodeURIComponent(apiKey)}&pageSize=1`);
    if (res.ok) {
      const data = (await res.json()) as { models?: unknown[] };
      return { ok: true, message: 'Key accepted by Google AI Studio.' };
    }
    if (res.status === 400 || res.status === 403) {
      return { ok: false, message: 'Key rejected — check the value and that the API is enabled.' };
    }
    return { ok: false, message: `Unexpected response: HTTP ${res.status}` };
  } catch {
    return { ok: false, message: 'Network error while contacting Google AI Studio.' };
  }
}

export function requireApiKey(): string {
  const key = useStore.getState().settings.apiKey.trim();
  if (!key) {
    throw new GeminiError('No API key configured. Add your Google AI Studio key in Settings.');
  }
  return key;
}
